/**
 * Run type predicates, and the two workflow values a run type resolves to.
 *
 * `MatchKit` used to be one value doing two jobs: the run type a study is performed under, and
 * the spec block it is graded against. Those have diverged — a study is run as either a
 * Calibrator or a Control Match Kit, while reference data still specifies a single "Match Kit"
 * block that both are graded against. So there are two resolutions, and which one a caller wants
 * depends on what it is talking to:
 *
 * - `toApiWorkflow` — the run type, for anything that records or filters what the study *is*.
 * - `toReferenceWorkflow` — the spec block, for anything matched against reference data.
 *
 * This mirrors `WorkFlow` and `reference_workflow()` in the API's `db/enums.py`; the two must
 * agree on the mapping or spec lookups miss silently.
 */

/**
 * Workflow the two match kit run types are specified by in reference data.
 *
 * Not a run type: it is no longer selectable as one, and is never saved as a study's run type.
 * It remains the value of the `testSpec` sent on a calculate request.
 */
export const MATCH_KIT_REFERENCE_WORKFLOW = 'MatchKit';

/** Run type for a control tested across many calibrator lots. */
export const CONTROL_MATCH_KIT_RUN_TYPE = 'ControlMatchKit';

/** Run type for a calibrator tested across many control lots. */
export const CALIBRATOR_MATCH_KIT_RUN_TYPE = 'CalibratorMatchKit';

/** Run type for a stability study. */
export const STABILITY_RUN_TYPE = 'Stability';

/** Run type for raw material QC. */
export const RMQC_RUN_TYPE = 'RMQC';

/** Run type for a pre-bulk coupling run. */
export const PREBULK_RUN_TYPE = 'PreBulk';

/** Run type for adjusting an existing calibrator lot's value assignments. */
export const CALIBRATOR_ADJUSTMENT_RUN_TYPE = 'CalibratorAdjustment';

/**
 * How a Calibrator Adjustment run treats its value assignments. Mirrors `CalAdjustmentMode` in the
 * API's `db/enums.py`; unrelated to the Final formulation stage.
 *
 * - `Adjust` dual-runs the carried-forward values against the new ones.
 * - `Final` runs once, treating the supplied values as the finalized lot's own.
 */
export type CalAdjustmentMode = 'Adjust' | 'Final';

/**
 * Reduces a run type to a comparison key, so that spacing and casing variants of the same
 * run type compare equal (`Control Match Kit`, `control_match_kit`, `ControlMatchKit`).
 */
const toComparisonKey = (runType?: string | null): string =>
  (runType ?? '').trim().replace(/[\s_-]+/g, '').toLowerCase();

/**
 * @param runType Run type recorded on the study
 * @returns `true` for the Control Match Kit run type
 */
export function isControlMatchKitRunType(runType?: string | null): boolean {
  return toComparisonKey(runType) === toComparisonKey(CONTROL_MATCH_KIT_RUN_TYPE);
}

/**
 * @param runType Run type recorded on the study
 * @returns `true` for the Calibrator Match Kit run type
 */
export function isCalibratorMatchKitRunType(runType?: string | null): boolean {
  return toComparisonKey(runType) === toComparisonKey(CALIBRATOR_MATCH_KIT_RUN_TYPE);
}

/**
 * @param runType Run type recorded on the study
 * @returns `true` for the Stability run type
 */
export function isStabilityRunType(runType?: string | null): boolean {
  return toComparisonKey(runType) === toComparisonKey(STABILITY_RUN_TYPE);
}

/**
 * @param runType Run type recorded on the study
 * @returns `true` for the RMQC run type
 */
export function isRmqcRunType(runType?: string | null): boolean {
  return toComparisonKey(runType) === toComparisonKey(RMQC_RUN_TYPE);
}

/**
 * @param runType Run type recorded on the study
 * @returns `true` for the PreBulk run type
 */
export function isPreBulkRunType(runType?: string | null): boolean {
  return toComparisonKey(runType) === toComparisonKey(PREBULK_RUN_TYPE);
}

/**
 * @param runType Run type recorded on the study
 * @returns `true` for the Calibrator Assignment run type
 */
export function isCalibratorAssignmentRunType(runType?: string | null): boolean {
  return toComparisonKey(runType) === 'calibratorassignment';
}

/**
 * @param runType Run type recorded on the study
 * @returns `true` for the Calibrator Adjustment run type
 */
export function isCalibratorAdjustmentRunType(runType?: string | null): boolean {
  return toComparisonKey(runType) === toComparisonKey(CALIBRATOR_ADJUSTMENT_RUN_TYPE);
}

/**
 * @param study The study's run type and Calibrator Adjustment mode
 * @returns `true` when a Calibrator Adjustment study holds Final-mode results
 */
export function isCalAdjustFinal(
  study?: { runType?: string | null; cal_adjustment_mode?: CalAdjustmentMode | null } | null
): boolean {
  return isCalibratorAdjustmentRunType(study?.runType) && study?.cal_adjustment_mode === 'Final';
}

/**
 * Whether the run type is either kind of match kit run.
 *
 * The bare `MatchKit` does not count: it is a reference-data workflow rather than a run type,
 * so a study is never performed under it.
 *
 * @param runType Run type recorded on the study
 * @returns `true` for Control Match Kit or Calibrator Match Kit
 */
export function isMatchKitRunType(runType?: string | null): boolean {
  return isControlMatchKitRunType(runType) || isCalibratorMatchKitRunType(runType);
}

/**
 * Maps a run type to the value the API accepts for it.
 *
 * Every run type keeps its own identity here, including both match kit run types — the API
 * accepts them by name and resolves the spec block itself. Spaces are removed because that is
 * the form the run type is stored and filtered under.
 *
 * Use this for anything that records or asks about what the study *is*: the calculate request's
 * `workflow`, the instrument upload's `workflow`, the panel specs path, and the studies list
 * filter. That last one matches the stored run type as a plain string with no validation behind
 * it, so sending the wrong value there returns an empty list rather than an error.
 *
 * @param runType Run type recorded on the study
 * @returns The run type as the API accepts it
 */
export function toApiWorkflow(runType?: string | null): string {
  return (runType ?? '').replace(/\s+/g, '');
}

/**
 * Maps a run type to the workflow its reference data is filed under.
 *
 * Both match kit run types are specified by the single "Match Kit" block, so both resolve to it;
 * every other run type specifies itself. The mirror of `reference_workflow()` in the API's
 * `db/enums.py`.
 *
 * Use this only for values matched against reference data — spec rows come back labelled with
 * the block they are filed under, not with the run type that asked for them.
 *
 * @param runType Run type recorded on the study
 * @returns The workflow whose specifications apply to the run type
 */
export function toReferenceWorkflow(runType?: string | null): string {
  return isMatchKitRunType(runType) ? MATCH_KIT_REFERENCE_WORKFLOW : toApiWorkflow(runType);
}
